// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * YAML for panel specs, both directions.
 *
 * The project carries no runtime dependencies, so rather than pull in a full
 * YAML implementation this handles the subset a panel spec actually uses:
 * block mappings and sequences, flow mappings and sequences, comments, and
 * plain or quoted scalars.
 *
 * Everything outside that subset is **rejected by name and line number** rather
 * than half-understood. Anchors and aliases in particular are refused: they are
 * the part of YAML most likely to be silently misread, and a spec that quietly
 * means something other than it says would produce a wrong panel.
 *
 * Emitted output must load under Python's `yaml.safe_load`, since that is what
 * the engine uses (`sigmaKi.py:116-126`).
 */

import { SPEC_KEY_ORDER, type PanelSpec } from './spec.ts';

export class YamlError extends Error {
  readonly line: number;

  constructor(message: string, line: number) {
    super(line > 0 ? `line ${line}: ${message}` : message);
    this.name = 'YamlError';
    this.line = line;
  }
}

// --- emitting -----------------------------------------------------------------------

/** Longest flow rendering that is still more readable than a block. */
const FLOW_LIMIT = 60;

const PLAIN_SAFE = /^[A-Za-z_][A-Za-z0-9_.\-/ ]*$/;
const LOOKS_NUMERIC = /^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/;

/**
 * The literals PyYAML resolves to booleans and null, spelled exactly as its own
 * resolver does.
 *
 * This has to agree with the engine or the two tools read the same file
 * differently: `set_origin: yes` is `True` to PyYAML, and treating it as the
 * string "yes" here would show one thing in the editor and build another.
 */
const TRUE_LITERALS = new Set(['yes', 'Yes', 'YES', 'true', 'True', 'TRUE', 'on', 'On', 'ON']);
const FALSE_LITERALS = new Set(['no', 'No', 'NO', 'false', 'False', 'FALSE', 'off', 'Off', 'OFF']);
const NULL_LITERALS = new Set(['null', 'Null', 'NULL', '~']);

/** Words that must be quoted when they are meant as strings. */
const RESERVED = new Set(
  [...TRUE_LITERALS, ...FALSE_LITERALS, ...NULL_LITERALS].map((s) => s.toLowerCase()),
);

function quote(s: string): string {
  return `"${s.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
}

function emitScalar(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) throw new Error(`cannot emit non-finite number ${v}`);
    // Trim binary-float noise: 0.30000000000000004 helps nobody.
    return String(Math.round(v * 1e9) / 1e9);
  }
  const s = String(v);
  if (s === '') return "''";
  if (!PLAIN_SAFE.test(s)) return quote(s);
  if (LOOKS_NUMERIC.test(s)) return quote(s);
  if (RESERVED.has(s.toLowerCase())) return quote(s);
  if (s !== s.trim()) return quote(s);
  return s;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** A one-line rendering, or null when the value cannot sensibly be inlined. */
function tryFlow(v: unknown): string | null {
  if (Array.isArray(v)) {
    const parts: string[] = [];
    for (const item of v) {
      const s = tryFlow(item);
      if (s === null) return null;
      parts.push(s);
    }
    return `[${parts.join(', ')}]`;
  }
  if (isPlainObject(v)) {
    const parts: string[] = [];
    for (const [k, val] of entriesOf(v)) {
      if (val === undefined) continue;
      const s = tryFlow(val);
      if (s === null) return null;
      parts.push(`${emitKey(k)}: ${s}`);
    }
    return `{${parts.join(', ')}}`;
  }
  return emitScalar(v);
}

function emitKey(k: string): string {
  return PLAIN_SAFE.test(k) && !RESERVED.has(k.toLowerCase()) ? k : quote(k);
}

/** Spec order first, then anything unrecognised, so unknown keys survive. */
function entriesOf(obj: Record<string, unknown>): Array<[string, unknown]> {
  const keys = Object.keys(obj);
  const known = SPEC_KEY_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !SPEC_KEY_ORDER.includes(k));
  const ordered = known.length > 0 ? [...known, ...rest] : keys;
  return ordered.map((k) => [k, obj[k]]);
}

function emitValue(v: unknown, indent: number): string {
  const pad = ' '.repeat(indent);

  if (Array.isArray(v)) {
    if (v.length === 0) return ' []';

    // Only a list of plain scalars collapses onto one line. `position: [20, 20]`
    // reads better inline, but folding the whole of `boards:` into one line
    // would make every edit a one-line diff of the entire panel.
    if (v.every((item) => !Array.isArray(item) && !isPlainObject(item))) {
      const flow = tryFlow(v);
      if (flow !== null && flow.length + indent <= FLOW_LIMIT) return ` ${flow}`;
    }

    const lines: string[] = [];
    for (const item of v) {
      const itemFlow = tryFlow(item);
      if (itemFlow !== null && itemFlow.length + indent + 2 <= FLOW_LIMIT) {
        lines.push(`${pad}- ${itemFlow}`);
      } else if (isPlainObject(item)) {
        // `- ` then the mapping, whose keys line up two columns further in.
        const body = emitMapping(item, indent + 2);
        lines.push(`${pad}- ${body.slice(indent + 2)}`);
      } else {
        lines.push(`${pad}-${emitValue(item, indent + 2)}`);
      }
    }
    return `\n${lines.join('\n')}`;
  }

  if (isPlainObject(v)) {
    if (Object.keys(v).length === 0) return ' {}';
    const flow = tryFlow(v);
    if (flow !== null && flow.length + indent <= FLOW_LIMIT) return ` ${flow}`;
    return `\n${emitMapping(v, indent)}`;
  }

  return ` ${emitScalar(v)}`;
}

function emitMapping(obj: Record<string, unknown>, indent: number): string {
  const pad = ' '.repeat(indent);
  const lines: string[] = [];
  for (const [k, v] of entriesOf(obj)) {
    if (v === undefined) continue;
    lines.push(`${pad}${emitKey(k)}:${emitValue(v, indent + 2)}`);
  }
  return lines.join('\n');
}

/**
 * Serialise a spec.
 *
 * `ensureDefaults` adds the `version` and `units` the engine expects, which is
 * wanted for a whole document and not for the raw-YAML fragments the inspector
 * edits in place.
 */
export function emitYaml(spec: PanelSpec, ensureDefaults = true): string {
  const doc: Record<string, unknown> = ensureDefaults
    ? { version: spec.version ?? 1, units: spec.units ?? 'mm', ...spec }
    : { ...spec };
  const body = emitMapping(doc, 0);
  return body === '' ? '' : `${body}\n`;
}

/** A fragment, for the raw-YAML textareas — no document keys injected. */
export function emitYamlFragment(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value) && value.length === 0) return '[]\n';
  if (Array.isArray(value)) return `${emitValue(value, 0).replace(/^\n/, '')}\n`;
  if (isPlainObject(value)) return `${emitMapping(value, 0)}\n`;
  return `${emitScalar(value)}\n`;
}

// --- parsing ------------------------------------------------------------------------

interface Line {
  indent: number;
  text: string;
  /** 1-based, for error messages. */
  number: number;
}

/** Strip a trailing comment, respecting quotes. */
function stripComment(raw: string): string {
  let quoteChar: '"' | "'" | null = null;
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i]!;
    if (quoteChar) {
      if (c === '\\' && quoteChar === '"') i++;
      else if (c === quoteChar) quoteChar = null;
      continue;
    }
    if (c === '"' || c === "'") {
      quoteChar = c;
      continue;
    }
    // A `#` only opens a comment at the start or after whitespace.
    if (c === '#' && (i === 0 || /\s/.test(raw[i - 1]!))) return raw.slice(0, i);
  }
  return raw;
}

function scanLines(text: string): Line[] {
  const out: Line[] = [];
  text.split(/\r?\n/).forEach((raw, i) => {
    const number = i + 1;
    if (/^\s*\t/.test(raw)) {
      throw new YamlError('tabs cannot be used for indentation', number);
    }
    const stripped = stripComment(raw).replace(/\s+$/, '');
    if (stripped.trim() === '') return;

    const trimmed = stripped.trimStart();
    if (trimmed === '---' || trimmed === '...') {
      throw new YamlError('multi-document YAML is not supported', number);
    }
    out.push({ indent: stripped.length - trimmed.length, text: trimmed, number });
  });
  return out;
}

function rejectUnsupported(text: string, line: number): void {
  if (/(^|\s)[&*][A-Za-z0-9_-]+/.test(text)) {
    throw new YamlError(
      'YAML anchors and aliases are not supported; write the value out in full',
      line,
    );
  }
  if (/^<<\s*:/.test(text)) {
    throw new YamlError('YAML merge keys (<<) are not supported', line);
  }
}

/** Split `key: rest`, honouring quotes and flow collections. */
function splitKey(text: string): { key: string; rest: string } | null {
  let quoteChar: '"' | "'" | null = null;
  let depth = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoteChar) {
      if (c === '\\' && quoteChar === '"') i++;
      else if (c === quoteChar) quoteChar = null;
      continue;
    }
    if (c === '"' || c === "'") quoteChar = c;
    else if (c === '{' || c === '[') depth++;
    else if (c === '}' || c === ']') depth--;
    else if (c === ':' && depth === 0) {
      const next = text[i + 1];
      if (next === undefined || next === ' ') {
        return { key: text.slice(0, i).trim(), rest: text.slice(i + 1).trim() };
      }
    }
  }
  return null;
}

function unquote(s: string, line: number): string {
  const q = s[0]!;
  const body = s.slice(1, -1);
  if (q === "'") return body.replace(/''/g, "'");
  let out = '';
  for (let i = 0; i < body.length; i++) {
    const c = body[i]!;
    if (c !== '\\') {
      out += c;
      continue;
    }
    const n = body[++i];
    if (n === 'n') out += '\n';
    else if (n === 't') out += '\t';
    else if (n === 'r') out += '\r';
    else if (n === '"' || n === '\\' || n === "'") out += n;
    else if (n === undefined) throw new YamlError('string ends with a dangling backslash', line);
    else out += n;
  }
  return out;
}

function parseScalar(text: string, line: number): unknown {
  const s = text.trim();
  if (s === '') return null;
  if ((s.startsWith('"') && s.endsWith('"') && s.length > 1) ||
      (s.startsWith("'") && s.endsWith("'") && s.length > 1)) {
    return unquote(s, line);
  }
  if (s === '|' || s === '>' || s.startsWith('|') || s.startsWith('>')) {
    throw new YamlError('block scalars (| and >) are not supported', line);
  }
  if (NULL_LITERALS.has(s)) return null;
  if (TRUE_LITERALS.has(s)) return true;
  if (FALSE_LITERALS.has(s)) return false;
  if (LOOKS_NUMERIC.test(s)) return Number(s);
  return s;
}

/** Parse a `{...}` or `[...]` value that sits entirely on one line. */
function parseFlow(text: string, line: number): unknown {
  let i = 0;

  const skipSpace = (): void => {
    while (i < text.length && /\s/.test(text[i]!)) i++;
  };

  const readScalarUntil = (stops: string): string => {
    let quoteChar: '"' | "'" | null = null;
    const start = i;
    while (i < text.length) {
      const c = text[i]!;
      if (quoteChar) {
        if (c === '\\' && quoteChar === '"') i++;
        else if (c === quoteChar) quoteChar = null;
      } else if (c === '"' || c === "'") {
        quoteChar = c;
      } else if (stops.includes(c)) {
        break;
      }
      i++;
    }
    if (quoteChar) throw new YamlError('unterminated quoted string', line);
    return text.slice(start, i).trim();
  };

  const value = (): unknown => {
    skipSpace();
    const c = text[i];
    if (c === '[') {
      i++;
      const arr: unknown[] = [];
      skipSpace();
      if (text[i] === ']') {
        i++;
        return arr;
      }
      for (;;) {
        arr.push(value());
        skipSpace();
        if (text[i] === ',') {
          i++;
          continue;
        }
        if (text[i] === ']') {
          i++;
          return arr;
        }
        throw new YamlError("expected ',' or ']' in a flow sequence", line);
      }
    }
    if (c === '{') {
      i++;
      const obj: Record<string, unknown> = {};
      skipSpace();
      if (text[i] === '}') {
        i++;
        return obj;
      }
      for (;;) {
        skipSpace();
        const rawKey = readScalarUntil(':,}');
        if (text[i] !== ':') throw new YamlError("expected ':' in a flow mapping", line);
        i++;
        const key = String(parseScalar(rawKey, line));
        obj[key] = value();
        skipSpace();
        if (text[i] === ',') {
          i++;
          continue;
        }
        if (text[i] === '}') {
          i++;
          return obj;
        }
        throw new YamlError("expected ',' or '}' in a flow mapping", line);
      }
    }
    return parseScalar(readScalarUntil(',]}'), line);
  };

  const result = value();
  skipSpace();
  if (i < text.length) {
    throw new YamlError(`unexpected ${JSON.stringify(text.slice(i))} after a flow value`, line);
  }
  return result;
}

function parseInline(text: string, line: number): unknown {
  rejectUnsupported(text, line);
  const s = text.trim();
  if (s.startsWith('{') || s.startsWith('[')) return parseFlow(s, line);
  return parseScalar(s, line);
}

interface Cursor {
  i: number;
}

function parseBlock(lines: Line[], cursor: Cursor, indent: number): unknown {
  const first = lines[cursor.i];
  if (first === undefined || first.indent < indent) return null;

  // A whole document (or nested value) can be a single flow collection.
  if (first.text.startsWith('[') || first.text.startsWith('{')) {
    cursor.i++;
    return parseInline(first.text, first.number);
  }

  return first.text.startsWith('- ') || first.text === '-'
    ? parseSequence(lines, cursor, first.indent)
    : parseMapping(lines, cursor, first.indent);
}

function parseMapping(lines: Line[], cursor: Cursor, indent: number): Record<string, unknown> {
  const obj: Record<string, unknown> = {};

  while (cursor.i < lines.length) {
    const line = lines[cursor.i]!;
    if (line.indent < indent) break;
    if (line.indent > indent) {
      throw new YamlError(`unexpected indentation in a mapping`, line.number);
    }
    rejectUnsupported(line.text, line.number);

    const split = splitKey(line.text);
    if (split === null) {
      throw new YamlError(`expected 'key: value' but found ${JSON.stringify(line.text)}`, line.number);
    }
    const key = String(parseScalar(split.key, line.number));
    cursor.i++;

    if (split.rest !== '') {
      obj[key] = parseInline(split.rest, line.number);
      continue;
    }

    // An empty value means the real one is nested below, or absent.
    const next = lines[cursor.i];
    if (next !== undefined && next.indent > indent) {
      obj[key] = parseBlock(lines, cursor, next.indent);
    } else if (next !== undefined && next.indent === indent && next.text.startsWith('- ')) {
      // A sequence may sit at the same column as its key, which is legal YAML.
      obj[key] = parseSequence(lines, cursor, indent);
    } else {
      obj[key] = null;
    }
  }

  return obj;
}

function parseSequence(lines: Line[], cursor: Cursor, indent: number): unknown[] {
  const out: unknown[] = [];

  while (cursor.i < lines.length) {
    const line = lines[cursor.i]!;
    if (line.indent < indent) break;
    if (line.indent > indent) {
      throw new YamlError('unexpected indentation in a sequence', line.number);
    }
    if (!line.text.startsWith('- ') && line.text !== '-') break;
    rejectUnsupported(line.text, line.number);

    const rest = line.text === '-' ? '' : line.text.slice(2).trim();
    // Keys of an inline mapping line up where the item's own text starts.
    const innerIndent = indent + 2;
    cursor.i++;

    if (rest === '') {
      const next = lines[cursor.i];
      out.push(next !== undefined && next.indent > indent ? parseBlock(lines, cursor, next.indent) : null);
      continue;
    }

    const split = rest.startsWith('{') || rest.startsWith('[') ? null : splitKey(rest);
    if (split === null) {
      out.push(parseInline(rest, line.number));
      continue;
    }

    // `- key: value`, possibly continued by more keys on following lines.
    const item: Record<string, unknown> = {};
    const key = String(parseScalar(split.key, line.number));
    if (split.rest !== '') {
      item[key] = parseInline(split.rest, line.number);
    } else {
      const next = lines[cursor.i];
      item[key] =
        next !== undefined && next.indent > innerIndent ? parseBlock(lines, cursor, next.indent) : null;
    }

    const next = lines[cursor.i];
    if (next !== undefined && next.indent === innerIndent && !next.text.startsWith('- ')) {
      Object.assign(item, parseMapping(lines, cursor, innerIndent));
    }
    out.push(item);
  }

  return out;
}

/** Parse the supported YAML subset. Throws `YamlError` with a line number. */
export function parseYaml(text: string): unknown {
  const lines = scanLines(text);
  if (lines.length === 0) return null;
  const cursor: Cursor = { i: 0 };
  const value = parseBlock(lines, cursor, lines[0]!.indent);
  if (cursor.i < lines.length) {
    throw new YamlError('trailing content after the document', lines[cursor.i]!.number);
  }
  return value;
}

/** Parse a spec from `.yaml`/`.yml` text, or `.json`, which the engine also takes. */
export function parseSpecFile(text: string, fileName: string): PanelSpec {
  const value = /\.json$/i.test(fileName) ? JSON.parse(text) : parseYaml(text);
  if (!isPlainObject(value)) {
    throw new YamlError('a panel spec must be a mapping at the top level', 0);
  }
  return value as PanelSpec;
}
